import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '@/features/app/hooks';
import { downloadMapaMensal, downloadMapaMensalPdf, useMapaMensal } from '@/features/mapas/hooks';
import type { MapaMensal } from '@/types';
import {
  addDays,
  formatDiaCurto,
  formatHoras,
  initials,
  isWeekend,
  monthOf,
  parseISODate,
  pct,
  todayISO,
} from '@/lib/format';
import type { Language } from '@/i18n';
import { AppLayout } from '@/components/AppLayout';
import { MonthNavigator } from '@/components/MonthNavigator';
import { BarrasHorizontais } from '@/components/charts/BarrasHorizontais';
import { ChartCard, DataTable, Legend } from '@/components/charts/ChartCard';
import { ColunasDiarias, type ColunaDia } from '@/components/charts/ColunasDiarias';
import { ComposicaoEmpilhada, type LinhaComposicao } from '@/components/charts/ComposicaoEmpilhada';
import { HeatmapEntregas, type LinhaHeatmap } from '@/components/charts/HeatmapEntregas';
import { StatTile } from '@/components/charts/StatTile';
import { buildObraColors, MAX_SERIES, OUTRAS } from '@/components/charts/palette';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { Icon } from '@/components/ui/Icon';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/components/ui/Toast';

/**
 * The chefia dashboard. Four charts, each answering one question:
 * where did the month's hours go, how did they land day by day, how did each
 * eletricista split their time, and who is missing partes diárias.
 */
export function EquipaPage() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage ?? 'pt') as Language;
  const navigate = useNavigate();
  const toast = useToast();
  const [mes, setMes] = useState(monthOf(todayISO()));
  const [verUser, setVerUser] = useState<string | null>(null);

  const settings = useSettings();
  const mapa = useMapaMensal(mes, verUser ?? undefined);

  const exportar = async (formato: 'csv' | 'pdf') => {
    try {
      if (formato === 'pdf') await downloadMapaMensalPdf(mes, verUser ?? undefined);
      else await downloadMapaMensal(mes, verUser ?? undefined);
      toast.show(t(formato === 'pdf' ? 'equipa.exportadoPdf' : 'equipa.exportado'));
    } catch (err) {
      toast.show(err instanceof Error ? err.message : t('common.error'));
    }
  };

  const detalhe = verUser
    ? mapa.data?.porPessoa.find((p) => p.userId === verUser)
    : undefined;

  return (
    <AppLayout title={t('header.equipa')} subtitle={t('header.acessoTotal')}>
      <div className="flex flex-col gap-4">
        {verUser && (
          <button
            type="button"
            onClick={() => setVerUser(null)}
            className="flex items-center gap-1.5 self-start rounded-full border border-conduril-border bg-white py-2 pl-2.5 pr-3.5 text-[11px] font-semibold text-conduril"
          >
            <Icon name="arrow_back" className="text-[16px]" /> {t('equipa.voltar')}
          </button>
        )}

        <div className="md:max-w-sm">
          <MonthNavigator
            mes={mes}
            onChange={setMes}
          scope={
            detalhe
              ? detalhe.nome
                : t('equipa.eletricistas', { count: mapa.data?.porPessoa.length ?? 0 })
            }
          />
        </div>

        {mapa.isLoading && (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        )}
        {mapa.error && <ErrorBanner error={mapa.error} onRetry={() => void mapa.refetch()} />}

        {mapa.data && (
          <>
            <section className="rounded-2xl bg-conduril p-4 md:p-5">
              <div className="flex items-end justify-between gap-3">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#94a1dd]">
                    {detalhe ? t('equipa.horasMes', { mes: '' }).trim() : t('equipa.horasEquipa')}
                  </div>
                  <div className="num mt-2.5 text-[30px] font-semibold leading-none text-white">
                    {formatHoras(mapa.data.totalHoras)}
                  </div>
                  <div className="mt-1.5 text-[11px] text-[#b3bce8]">
                    {t('mapa.cumprimento', { pct: pct(mapa.data.totalHoras, mapa.data.horasAlvo) })}
                  </div>
                </div>
                <div className="flex shrink-0 flex-col gap-1.5 sm:flex-row">
                  <button
                    type="button"
                    onClick={() => void exportar('pdf')}
                    className="flex items-center justify-center gap-1.5 rounded-xl bg-white px-3 py-2.5 text-[11px] font-semibold text-conduril transition hover:bg-white/90"
                  >
                    <Icon name="picture_as_pdf" className="text-[16px]" /> {t('common.pdf')}
                  </button>
                  <button
                    type="button"
                    onClick={() => void exportar('csv')}
                    className="flex items-center justify-center gap-1.5 rounded-xl border border-white/20 bg-white/15 px-3 py-2.5 text-[11px] font-semibold text-white transition hover:bg-white/25"
                  >
                    <Icon name="download" className="text-[16px]" /> {t('common.csv')}
                  </button>
                </div>
              </div>
            </section>

            {mapa.data.porObra.length === 0 ? (
              <EmptyState message={t('mapa.semDados')} />
            ) : (
              <Dashboard
                mapa={mapa.data}
                lang={lang}
                alvoDiario={settings.data?.horasAlvoDiarias ?? 8}
                soUmaPessoa={Boolean(verUser)}
                onSelectPessoa={setVerUser}
                onSelectDia={(data) => navigate(`/hoje?data=${data}`)}
              />
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}

interface DashboardProps {
  mapa: MapaMensal;
  lang: Language;
  alvoDiario: number;
  soUmaPessoa: boolean;
  onSelectPessoa: (userId: string) => void;
  onSelectDia: (data: string) => void;
}

function Dashboard({
  mapa,
  lang,
  alvoDiario,
  soUmaPessoa,
  onSelectPessoa,
  onSelectDia,
}: DashboardProps) {
  const { t } = useTranslation();

  // One colour per obra, assigned by monthly rank and reused by every chart on
  // the page, so a filter never repaints the survivors.
  const obraColors = useMemo(
    () => buildObraColors(mapa.porObra.map((o) => o.obraId)),
    [mapa.porObra],
  );
  const obraNome = useMemo(
    () => new Map(mapa.porObra.map((o) => [o.obraId, o.nome])),
    [mapa.porObra],
  );

  // Every calendar day of the month, so gaps read as gaps.
  const dias = useMemo(() => {
    const out: string[] = [];
    for (let d = mapa.primeiroDia; d <= mapa.ultimoDia; d = addDays(d, 1)) out.push(d);
    return out;
  }, [mapa.primeiroDia, mapa.ultimoDia]);

  const porDiaMap = useMemo(() => new Map(mapa.porDia.map((d) => [d.data, d])), [mapa.porDia]);

  const colunas: ColunaDia[] = dias.map((data) => {
    const dia = porDiaMap.get(data);
    return {
      data,
      dia: parseISODate(data).getDate(),
      horas: dia?.horas ?? 0,
      horasLabel: formatHoras(dia?.horas ?? 0),
      fimDeSemana: isWeekend(data),
      detalhe: dia
        ? `${formatDiaCurto(data, lang)} · ${t('mapa.detalheDia', { obras: dia.obras, pessoas: dia.pessoas })}`
        : formatDiaCurto(data, lang),
    };
  });

  const pessoasAtivas = mapa.porPessoa.filter((p) => p.horas > 0 || p.diasUteisSemParte > 0);
  const alvoLinha = soUmaPessoa ? alvoDiario : alvoDiario * Math.max(1, pessoasAtivas.length);

  // Stacked composition: top obras keep their colour, the tail folds into "Outras".
  const composicao: LinhaComposicao[] = pessoasAtivas
    .filter((p) => p.horas > 0)
    .map((pessoa) => {
      const seus = mapa.porPessoaObra
        .filter((x) => x.userId === pessoa.userId)
        .sort((a, b) => b.horas - a.horas);

      const nomeados = seus.filter((x) => obraColors.has(x.obraId));
      const resto = seus.filter((x) => !obraColors.has(x.obraId));
      const restoHoras = resto.reduce((sum, x) => sum + x.horas, 0);

      const segmentos = nomeados.map((x) => ({
        id: x.obraId,
        label: obraNome.get(x.obraId) ?? '—',
        valor: x.horas,
        valorLabel: formatHoras(x.horas),
        color: obraColors.get(x.obraId) ?? OUTRAS,
      }));

      if (restoHoras > 0) {
        segmentos.push({
          id: `${pessoa.userId}-outras`,
          label: t('graficos.outras'),
          valor: restoHoras,
          valorLabel: formatHoras(restoHoras),
          color: OUTRAS,
        });
      }

      return {
        id: pessoa.userId,
        nome: pessoa.nome,
        total: pessoa.horas,
        totalLabel: formatHoras(pessoa.horas),
        segmentos,
      };
    });

  // Completeness heatmap: one row per eletricista, one cell per day.
  const porPessoaDia = useMemo(() => {
    const map = new Map<string, { horas: number; fechada: boolean }>();
    for (const cell of mapa.porPessoaDia) map.set(`${cell.userId}|${cell.data}`, cell);
    return map;
  }, [mapa.porPessoaDia]);

  const heatmap: LinhaHeatmap[] = pessoasAtivas.map((pessoa) => ({
    id: pessoa.userId,
    nome: pessoa.nome.split(' ')[0],
    celulas: dias.map((data) => {
      const cell = porPessoaDia.get(`${pessoa.userId}|${data}`);
      return {
        data,
        dia: parseISODate(data).getDate(),
        horas: cell?.horas ?? 0,
        horasLabel: formatHoras(cell?.horas ?? 0),
        fimDeSemana: isWeekend(data),
        fechada: cell?.fechada ?? false,
      };
    }),
  }));

  const legendaObras = mapa.porObra
    .slice(0, MAX_SERIES)
    .map((o) => ({ label: o.nome, color: obraColors.get(o.obraId) ?? OUTRAS }));
  if (mapa.porObra.length > MAX_SERIES) legendaObras.push({ label: t('graficos.outras'), color: OUTRAS });

  return (
    <>
      <div className="grid grid-cols-2 gap-2 md:gap-4 lg:max-w-2xl">
        <StatTile
          label={t('mapa.diasObras')}
          value={`${mapa.diasComRegisto} / ${mapa.obrasDistintas}`}
          sub={t('mapa.vsAlvo', { horas: formatHoras(mapa.horasAlvo) })}
        />
        <StatTile
          label={t('equipaDia.titulo')}
          value={String(pessoasAtivas.reduce((sum, p) => sum + p.diasUteisSemParte, 0))}
          sub={t('equipa.falta', { count: pessoasAtivas.reduce((s, p) => s + p.diasUteisSemParte, 0) })}
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-2 xl:items-start">
      <ChartCard
        title={t('graficos.porObra')}
        caption={t('graficos.porObraLegenda')}
        table={
          <DataTable
            headers={[t('graficos.tabelaObra'), t('graficos.tabelaHoras'), t('graficos.tabelaDia')]}
            rows={mapa.porObra.map((o) => [o.nome, formatHoras(o.horas), o.dias])}
          />
        }
      >
        <BarrasHorizontais
          items={mapa.porObra.map((o) => ({
            id: o.obraId,
            label: o.nome,
            sublabel: o.centroCusto,
            valor: o.horas,
            valorLabel: formatHoras(o.horas),
          }))}
        />
      </ChartCard>

      <ChartCard
        title={t('graficos.porDia')}
        caption={t('graficos.porDiaLegenda')}
        table={
          <DataTable
            headers={[t('graficos.tabelaDia'), t('graficos.tabelaHoras')]}
            rows={colunas.filter((c) => c.horas > 0).map((c) => [formatDiaCurto(c.data, lang), formatHoras(c.horas)])}
          />
        }
      >
        <ColunasDiarias
          dias={colunas}
          alvo={alvoLinha}
          alvoLabel={`${t('graficos.previsto')} ${formatHoras(alvoLinha)}`}
          onSelect={onSelectDia}
        />
      </ChartCard>

      {!soUmaPessoa && composicao.length > 0 && (
        <ChartCard
          title={t('graficos.composicao')}
          caption={t('graficos.composicaoLegenda')}
          legend={<Legend items={legendaObras} />}
          table={
            <DataTable
              headers={[t('graficos.tabelaEletricista'), t('graficos.tabelaHoras'), t('graficos.tabelaPeso')]}
              rows={composicao.map((l) => [
                l.nome,
                l.totalLabel,
                `${pct(l.total, mapa.totalHoras)}%`,
              ])}
            />
          }
        >
          <ComposicaoEmpilhada linhas={composicao} />
        </ChartCard>
      )}

      {!soUmaPessoa && heatmap.length > 0 && (
        <ChartCard
          title={t('graficos.completude')}
          caption={t('graficos.completudeLegenda')}
          table={
            <DataTable
              headers={[
                t('graficos.tabelaEletricista'),
                t('graficos.tabelaHoras'),
                t('graficos.semParte'),
              ]}
              rows={pessoasAtivas.map((p) => [p.nome, formatHoras(p.horas), p.diasUteisSemParte])}
            />
          }
        >
          <HeatmapEntregas linhas={heatmap} max={Math.ceil(alvoDiario)} />
        </ChartCard>
      )}
      </div>

      <div className="label-eyebrow mt-1">{t('equipa.porEletricista')}</div>
      <ul className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
        {mapa.porPessoa.map((pessoa) => (
          <li key={pessoa.userId}>
            <button
              type="button"
              onClick={() => onSelectPessoa(pessoa.userId)}
              className="w-full rounded-2xl border border-pessoa-border bg-pessoa-bg p-3.5 text-left shadow-card"
            >
              <span className="flex items-center justify-between gap-3">
                <span className="flex min-w-0 items-center gap-3">
                  <span className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-full bg-white text-[12px] font-semibold text-conduril">
                    {initials(pessoa.nome)}
                  </span>
                  <span className="flex min-w-0 flex-col gap-1">
                    <span className="truncate text-[14px] font-semibold leading-tight text-conduril-ink">
                      {pessoa.nome}
                    </span>
                    <span className="text-[11px] text-conduril-muted">
                      {t('equipa.detalhe', { dias: pessoa.dias, obras: pessoa.obras })}
                    </span>
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  <span className="num text-[16px] font-semibold text-conduril">
                    {formatHoras(pessoa.horas)}
                  </span>
                  <Icon name="chevron_right" className="text-[18px] text-[#b6bbc8]" />
                </span>
              </span>
              {pessoa.diasUteisSemParte > 0 && (
                <span className="mt-2.5 flex items-center gap-2 border-t border-pessoa-line pt-2.5">
                  <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-aviso-dot" />
                  <span className="text-[11px] font-medium text-[#8a6414]">
                    {t('equipa.falta', { count: pessoa.diasUteisSemParte })}
                  </span>
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}
